<script setup lang="ts">
    import ClientLogo from '@/components/ClientLogo.vue';
    import LocaleToggle from '@/components/LocaleToggle.vue';
    import { ERouteName } from '@/typescript/enums/ERouteName';
    import { useProducts } from '@/composables/useProducts';
    import { ref } from 'vue';
    import { useRouter } from 'vue-router';
    import { useI18n } from 'vue-i18n';
    import type { IProduct } from '@/domain/product/IProduct';

    const { t } = useI18n();

    const router = useRouter();
    const { error, isLoading, products } = useProducts();

    const isNavigating = ref(false);

    function onSelectProduct(product: IProduct) {
        isNavigating.value = true;
        router.push({ name: ERouteName.Configurator, params: { productName: product.name } });
    }
</script>

<template>
    <div
        class="relative flex h-full w-full flex-col items-center justify-center gap-6"
        style="background: linear-gradient(166.4deg, #fff 8.5%, #f5f5f7 50%, #fff 91.5%)"
    >
        <div class="absolute right-4 top-4">
            <LocaleToggle />
        </div>

        <div class="mb-4">
            <ClientLogo size="lg" />
        </div>

        <div class="flex flex-col items-center gap-3 px-4 text-center">
            <h1 class="font-input-serif text-2xl font-bold text-[#222] sm:text-4xl">
                {{ t('product_selection_title') }}
            </h1>
            <p class="text-sm text-[#82828c]">{{ t('product_selection_subtitle') }}</p>
        </div>

        <div
            v-if="isLoading"
            class="mt-6 flex w-full max-w-xl flex-col gap-4 px-4 sm:flex-row sm:gap-6"
        >
            <div class="rounded-card shadow-card flex-1 animate-pulse border border-[#e8e8ed] bg-[#f5f5f7] py-10" />
            <div class="rounded-card shadow-card flex-1 animate-pulse border border-[#e8e8ed] bg-[#f5f5f7] py-10" />
        </div>

        <p
            v-else-if="error !== undefined"
            class="text-sm text-red-500"
            data-test="product-list-error"
        >
            {{ error }}
        </p>

        <p
            v-else-if="products.length === 0"
            class="text-sm text-[#82828c]"
            data-test="product-list-empty"
        >
            {{ t('product_list_empty') }}
        </p>

        <div
            v-else
            class="mt-6 flex w-full max-w-xl flex-col justify-center gap-4 px-4 sm:flex-row sm:gap-6"
        >
            <button
                v-for="product in products"
                :key="product.id"
                class="rounded-card shadow-card hover:border-primary hover:shadow-dropdown flex flex-1 flex-row items-center gap-4 border border-[#e8e8ed] bg-white px-6 py-5 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-40 sm:flex-col sm:py-10"
                :disabled="isNavigating"
                data-test="product-card"
                @click="onSelectProduct(product)"
            >
                <div class="flex h-14 w-14 shrink-0 items-center justify-center sm:h-24 sm:w-24">
                    <svg
                        v-if="product.has3d"
                        xmlns="http://www.w3.org/2000/svg"
                        class="text-primary h-10 w-10 sm:h-20 sm:w-20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="0.9"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path d="M7 3h10l-2 13a2 2 0 0 1-2 2H11a2 2 0 0 1-2-2L7 3Z" />
                        <line
                            x1="7.5"
                            y1="6.5"
                            x2="16.5"
                            y2="6.5"
                        />
                    </svg>
                    <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        class="text-primary h-10 w-10 sm:h-20 sm:w-20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="0.9"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <rect
                            x="2"
                            y="4"
                            width="20"
                            height="16"
                            rx="2"
                        />
                        <path d="M3 5l9 9 9-9" />
                    </svg>
                </div>
                <div class="flex flex-col gap-1 text-left sm:items-center sm:text-center">
                    <span class="font-input-serif text-base font-bold text-[#222] sm:text-lg">{{ product.label }}</span>
                </div>
            </button>
        </div>
    </div>
</template>
